/**
 * What the executive modules keep on the platform: each module's entered
 * values under its own note topic, attributed and dated; the browser keeps a
 * copy so the screen works on the sample.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSession } from "../../app/SessionContext";
import { latestNote, putNote } from "../shared/noteStore";
import { t } from "../../i18n";

export interface Entered<T> {
  value: T;
  by?: string;
  at?: string;
  /** true until a note has been loaded or the platform said there is none */
  loading: boolean;
  savedTo: string | null;
  save: (next: T) => Promise<void>;
}

/** The latest note under `topic`, else `seed`; `save` writes the next one under the signed-in person. */
export function useEntered<T extends object>(topic: string, seed: T): Entered<T> {
  const session = useSession();
  const qc = useQueryClient();
  const source = session.source;
  const siteId = session.site.id;
  const who = session.principal.name;
  const q = useQuery({
    queryKey: ["exec-entered", siteId, topic],
    staleTime: 60_000,
    /* other people's saves arrive without a reload (audit 2026-10-10: shared registers never refreshed) */
    refetchInterval: 60_000,
    queryFn: () => latestNote<T & { enteredBy?: string; enteredAt?: string }>(source, siteId, topic),
  });
  /* a save shows at once; otherwise the latest note, else the seed — derived, not synchronised */
  const [local, setLocal] = useState<{ value: T; by?: string; at?: string; savedAt: number; browserOnly?: boolean } | null>(
    null,
  );
  const [savedTo, setSavedTo] = useState<string | null>(null);
  /*
   * A save shows at once, until the platform answers with anything newer —
   * this person's own save coming back, or a colleague's after it. It used to
   * win for as long as the screen stayed open, so after one save a screen
   * never showed anyone else's change and its next save wiped it.
   */
  const pinned = local && (local.browserOnly || !q.data || q.dataUpdatedAt < local.savedAt) ? local : null;
  const current =
    pinned ??
    (q.data
      ? { value: q.data.value, by: q.data.value.enteredBy ?? q.data.by, at: q.data.value.enteredAt ?? q.data.at }
      : { value: seed });
  return {
    value: current.value,
    by: current.by,
    at: current.at,
    loading: q.isPending,
    savedTo,
    save: async (next: T) => {
      const at = new Date().toISOString();
      const stamped = { ...next, enteredBy: who, enteredAt: at };
      setLocal({ value: stamped as T, by: who, at, savedAt: Date.now() });
      const where = await putNote(source, siteId, topic, stamped);
      /* refused by the platform: keep showing it, with the warning, rather than revert to the older platform copy */
      if (where === "browser") setLocal({ value: stamped as T, by: who, at, savedAt: Date.now(), browserOnly: true });
      setSavedTo(
        where === "platform"
          ? t("saved on the platform under {who}", { who })
          : t("saved in this browser only — the platform did not take the note"),
      );
      void qc.invalidateQueries({ queryKey: ["exec-entered", siteId, topic] });
    },
  };
}
